import {AxiosError} from "axios";
import {Bin as DeleteIcon, Refresh as ReleaseIcon, ShieldEye as DLPIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {QuarantineApi, QuarantinedMail} from "../api/admin/generated";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {DialogCloseCause} from "../common/Enums";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {Truncate} from "../common/Truncate";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {QuarantineReleaseDialog} from "./QuarantineReleaseDialog";
import {QuarantineViewEmailDialog} from "./QuarantineViewEmailDialog";

/*
 * REST api for accessing the back-end
 */
const quarantineApi = new QuarantineApi(ApiConfig.config());

type OnClick = () => void;

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className?: string
    maxLength?: number
    onClick? : OnClick
}

const Column = ({value, children, className, maxLength = 60, onClick}: ColumnProps) => {
    return (
        <td className={"user-select-all " + className} onClick={onClick}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const QuarantineQueue = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The mail queue items
     */
    const [queueItems, setQueueItems] = useState<QuarantinedMail[]>([]);
    /**
     * The total number of MTA queue items
     */
    const [queueSize, setQueueSize] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected queue item
     */
    const [selectedQueueItems, setSelectedQueueItems] = useState<QuarantinedMail[]>([]);
    /**
     * The clicked queue item
     */
    const [clickedQueueItem, setClickedQueueItem] = useState<QuarantinedMail>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "mail dialog" modal dialog will be shown
     */
    const [showMailDialog, setShowMailDialog] = useState<boolean>(false);
    /**
     * If true, the "release dialog" modal dialog will be shown
     */
    const [showReleaseDialog, setShowReleaseDialog] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        logger.debug("Load queue size")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        quarantineApi.quarantineControllerGetQuarantinedMailCount()
            .then(response => {
                setQueueSize(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue size", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load queue items")

        // reset state when we load new data
        setSelectedQueueItems([]);
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        quarantineApi.quarantineControllerGetQuarantinedMail((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setQueueItems(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue list", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, queueItem : QuarantinedMail) =>
    {
        setSelectedQueueItems(checked ?
            [...selectedQueueItems, queueItem] :
            selectedQueueItems.filter(q => q.id !== queueItem.id)
        );
    }

    const onClickQueueId = (queueItem : QuarantinedMail) =>
    {
        setClickedQueueItem(queueItem);
        setShowMailDialog(true);
    }

    const invertSelection = () => {
        setSelectedQueueItems([...queueItems].filter(q => !selectedQueueItems.includes(q)));
    }

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        quarantineApi.quarantineControllerDeleteQuarantinedMails(selectedQueueItems.map(q => q.id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedQueueItems([]);
            })
    }

    /**
     * Is called when the ViewEmailDialog is closed
     */
    const onViewEmailDialogClose = () => {
        setShowMailDialog(false);
    }

    /**
     * Is called when the ReleaseDialog is closed
     */
    const onReleaseDialogClose = (closeCause : DialogCloseCause) =>
    {
        setShowReleaseDialog(false);

        if (closeCause === DialogCloseCause.Succes)
        {
            setForceReload(true);
            setSelectedQueueItems([]);
        }
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("quarantine.are-you-sure-delete-items")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showMailDialog && clickedQueueItem?.id !== undefined && <QuarantineViewEmailDialog queueID={clickedQueueItem.id} onClose={onViewEmailDialogClose} />}
            {showReleaseDialog && <QuarantineReleaseDialog queueIDs={selectedQueueItems.map(q => q.id)} onClose={onReleaseDialogClose} />}


            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("quarantine.title")} icon={<DLPIcon />}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <thead className="sticky-top">

                        <tr className="text-nowrap">
                            {selectedQueueItems.length === 0 ?
                                <>
                                <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                <th>{t("quarantine.id")}</th>
                                <th>{t("quarantine.message-id")}</th>
                                <th>{t("quarantine.subject")}</th>
                                <th>{t("quarantine.from")}</th>
                                <th>{t("quarantine.sender")}</th>
                                <th>{t("quarantine.recipients")}</th>
                                <th>{t("quarantine.remote-address")}</th>
                                <th>{t("quarantine.created")}</th>
                                <th>{t("quarantine.policy-violations")}</th>
                                <th>{t("quarantine.error-message")}</th>
                                </>
                                :
                                <th colSpan={11}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedQueueItems([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedQueueItems.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link disabled={selectedQueueItems.length === 0}
                                                              onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger"/> {t("quarantine.delete-selected")}
                                            </Nav.Link>
                                            <Nav.Link disabled={selectedQueueItems.length === 0}
                                                              onClick={() => setShowReleaseDialog(true)}>
                                                <ReleaseIcon className="text-success"/> {t("quarantine.release-selected")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {queueItems?.map((q: QuarantinedMail) => (
                            <tr key={q.id} className="text-nowrap">
                                <Column className="td-w-min">
                                    <Form.Check
                                        type="checkbox"
                                        className="me-0"
                                        checked={selectedQueueItems.find((squeueitem) =>  squeueitem.id === q.id) !== undefined}
                                        onChange={(e) => onChangeSelect(e.target.checked, q)}
                                        inline
                                    />
                                </Column>
                                <Column value={q.id} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.messageID} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.subject} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.from} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.sender} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.recipients.join(", ")} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.remoteAddress} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={DateUtils.timestampToDateStringShort(q.created)} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.policyViolations.map(v => v.rule).join(", ")} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                                <Column value={q.policyViolationErrorMessage} onClick={() => onClickQueueId(q)} className="cursor-pointer"/>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {queueSize} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={queueSize}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}
